import { useState } from 'react';
import PropTypes from 'prop-types';
import { useUser } from '@auth0/nextjs-auth0';
import { useRouter } from 'next/router';

// ---------------------------------------------------------

import Button from '@components/button';
import AdvancedSearch from '@components/advanced-search';
import Filters from '@components/filters';
import Modal from '@components/modal';
import InputField from '@components/inputs/input/component';
import { convertQueryStringToRedux } from '@lib/search-results';
import { querifyLocation } from '@lib/query-utils';

// ---------------------------------------------------------

import styles from './styles.module.scss';
const {
  tag_container,
  tag,
  add_location_btn,
  partial_search_text,
  results_filters_container,
  results_headings_container,
  new_search_button,
  save_search_button
} = styles;

// ---------------------------------------------------------

const ResultsPageFilter = (props) => {
  let { headerValue, selections, setPageNum, handleSaveSearch } = props;

  const { user } = useUser();
  const router = useRouter();

  // Parse locations from URL query params
  const { convertedRouterParams } = convertQueryStringToRedux(router.query || {});
  const locations = convertedRouterParams.locations || [];

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [saveSearchModalIsOpen, setSaveSearchModalIsOpen] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [isNewSearch, setIsNewSearch] = useState(false);

  const handleRemoveOption = (option) => {
    const newLocations = locations.filter((el, idx) => idx !== option);
    // Rebuild URL query without the removed location
    const { agentid, counties, places, schoolDistricts, zip, ...restQuery } = router.query || {};
    const locationQuery = {};
    newLocations.forEach(({ value }) => {
      const locationString = querifyLocation(value);
      if (locationQuery[value.areaType]) {
        locationQuery[value.areaType].push(locationString);
      } else {
        locationQuery[value.areaType] = [locationString];
      }
    });
    router.push(
      { pathname: '/results', query: { ...restQuery, ...locationQuery } },
      undefined,
      { shallow: false }
    );
  };

  const handlePillsKeyDown = (e, option) => {
    if (e.key === 'Enter') handleRemoveOption(option);
  };

  // ---------------------------------------------------------

  const handleOpenModal = () => {
    setModalIsOpen(true);
    setIsNewSearch(false);
  };

  const handleNewSearchModal = () => {
    setModalIsOpen(true);
    setIsNewSearch(true);
  };

  const handleSaveSearchClick = () => {
    if (user) return setSaveSearchModalIsOpen(true);
    if (typeof window !== 'undefined') {
      window.location.href = '/auth/login';
    } else {
      router.push('/auth/login');
    }
  };

  const handleSaveSearchSubmit = () => {
    if (typeof handleSaveSearch === 'function') {
      handleSaveSearch(saveSearchName);
    }
    setSaveSearchModalIsOpen(false);
  };

  const newSearchButton = (
    <Button
      label="New search"
      className={new_search_button}
      theme="text-only"
      type="button"
      onClick={() => handleNewSearchModal()}
    />
  );

  return (
    <div>
      <div className={results_headings_container}>
        <h1>{headerValue}</h1>
        {newSearchButton}
      </div>

      <div className={tag_container}>
        {router.query.address && (
          <span className={partial_search_text}>Partial Street Search: {router.query.address}</span>
        )}
        {!router.query.address &&
          locations.map((el, idx) => (
            <button
              className={tag}
              key={el.label}
              tabIndex={0}
              onKeyDown={(e) => handlePillsKeyDown(e, idx)}
              onClick={() => handleRemoveOption(idx)}
              style={{ backgroundImage: 'url("/images/icons/icons-actions-close.svg")' }}
            >
              {el.label}
            </button>
          ))}
        {!router.query.address && (
          <Button
            className={add_location_btn}
            label="Add location or school district"
            theme="text-only"
            type="button"
            onClick={() => handleOpenModal()}
          />
        )}
      </div>

      <div className={results_filters_container}>
        <Filters />
        <div>
          {newSearchButton}
          <Button
            label="Save search"
            theme="text-only"
            type="button"
            onClick={handleSaveSearchClick}
          />
        </div>
      </div>

      {modalIsOpen && (
        <AdvancedSearch
          modalIsOpen={modalIsOpen}
          setModalIsOpen={setModalIsOpen}
          isNewSearch={isNewSearch}
          existingValues={selections}
          setPageNum={setPageNum}
        />
      )}

      <Modal
        modalIsOpen={saveSearchModalIsOpen}
        onModalClose={() => setSaveSearchModalIsOpen(false)}
      >
        <h2>Saving Search</h2>
        <InputField
          required
          label="Enter a name to find your search later"
          placeholder="My new search..."
          searchValueCallback={setSaveSearchName}
        />
        <Button
          className={save_search_button}
          label="Save Search"
          title="Save Search"
          onClick={handleSaveSearchSubmit}
          disabled={!saveSearchName}
        />
      </Modal>
    </div>
  );
};

ResultsPageFilter.propTypes = {
  filterChangeCallback: PropTypes.func,
  handleSaveSearch: PropTypes.func,
  headerValue: PropTypes.string,
  selections: PropTypes.any,
  selectionsCallback: PropTypes.func,
  setPageNum: PropTypes.func
};

export default ResultsPageFilter;
